import Paths from "@web/common/Paths";
import Root from "@web/main/Root";
import RootErrorBoundary from "@web/main/RootErrorBoundary";
import { DefaultAppRedirect } from "@web/main/routeGuards";
import SessionLayout from "@web/main/SessionLayout";
import { PublicTracking } from "@web/tracking/PublicTracking";
import type { ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import Config from "../config/Config";
import { registerRouter } from "./navigation";

// One dynamic-import thunk per chunk; routes sharing a thunk share a bundle chunk.
const appShell = () => import("./appShell.routes");
const messaging = () => import("../messaging/messaging.routes");
const tracking = () => import("../tracking/tracking.routes");
const settings = () => import("../settings/settings.routes");
const setup = () => import("../setup/setup.routes");

/** route.lazy() loader that picks one named export off a chunk module. */
function fromChunk<M extends Record<string, unknown>, K extends keyof M>(
	load: () => Promise<M>,
	key: K,
) {
	return async () => ({ Component: (await load())[key] as ComponentType });
}

export const router = createBrowserRouter(
	[
		{
			element: <Root />,
			errorElement: <RootErrorBoundary />,
			children: [
				{ path: Paths.PublicTrackingMatch, element: <PublicTracking /> },
				{
					element: <SessionLayout />,
					children: [
						{
							path: Paths.Setup,
							lazy: fromChunk(setup, "SetupLayout"),
							children: [
								{
									index: true,
									element: <Navigate to={Paths.Device} replace />,
								},
								{
									path: Paths.Device,
									lazy: fromChunk(setup, "DeviceRegistrationController"),
								},
								{
									path: `${Paths.SetupUser}/*`,
									lazy: fromChunk(setup, "UserInfoController"),
								},
								{ path: Paths.Plans, lazy: fromChunk(setup, "PlansController") },
								{
									path: Paths.Confirmation,
									lazy: fromChunk(setup, "AccountConfirmation"),
								},
								{ path: "*", element: <Navigate to={Paths.Device} replace /> },
							],
						},
						{
							path: Paths.App,
							lazy: fromChunk(appShell, "AppLayout"),
							children: [
								{ index: true, element: <DefaultAppRedirect /> },
								{
									path: Paths.ClaimOrganization,
									lazy: fromChunk(appShell, "OrganizationOnboardingFlow"),
								},
								{
									lazy: fromChunk(appShell, "MediaCommsLayout"),
									children: [
										...(Config.somewear.messages.enable
											? [
													{
														path: `${Paths.Conversations}/:key?/:messageId?`,
														lazy: fromChunk(messaging, "Messaging"),
													},
												]
											: []),
										{
											path: `${Paths.Tracking}/:id?`,
											lazy: fromChunk(tracking, "TrackingRoute"),
										},
										{
											path: Paths.Settings,
											lazy: fromChunk(settings, "SettingsLayout"),
											children: [
												{
													index: true,
													lazy: fromChunk(
														settings,
														"SettingsIndexRedirect",
													),
												},
												{
													lazy: fromChunk(settings, "RequirePaidUser"),
													children: [
														{
															path: Paths.SettingsEmergency,
															lazy: fromChunk(
																settings,
																"SettingsEmergencyController",
															),
														},
														{
															path: Paths.SettingsBilling,
															lazy: fromChunk(
																settings,
																"SettingsBillingController",
															),
														},
													],
												},
												{
													path: Paths.SettingsChangelog,
													lazy: fromChunk(
														settings,
														"ChangelogController",
													),
												},
												{
													path: Paths.SettingsDownloads,
													lazy: fromChunk(settings, "DownloadsPage"),
												},
												{
													path: Paths.SettingsProfile,
													lazy: fromChunk(
														settings,
														"SettingsProfileController",
													),
												},
												{
													path: `${Paths.SettingsJoinWorkspace}/:name?/:token?`,
													lazy: fromChunk(settings, "JoinWorkspaceRoute"),
												},
												{
													path: Paths.SettingsStaff,
													lazy: fromChunk(settings, "SettingsStaffRoute"),
												},
												{
													path: Paths.SettingsWorkspace,
													lazy: fromChunk(
														settings,
														"WorkspaceDashboardView",
													),
												},
												{
													path: `${Paths.SettingsWorkspace}/:slug/*`,
													lazy: fromChunk(
														settings,
														"WorkspaceDashboardView",
													),
												},
												{
													lazy: fromChunk(settings, "RequireOrgAdmin"),
													children: [
														{
															path: Paths.SettingsWorkspaces,
															lazy: fromChunk(
																settings,
																"OrganizationDashboardView",
															),
														},
														{
															path: Paths.SettingsOrganization,
															lazy: fromChunk(
																settings,
																"OrganizationDashboardView",
															),
														},
														{
															path: `${Paths.SettingsOrganization}/:slug/*`,
															lazy: fromChunk(
																settings,
																"OrganizationSlugRoute",
															),
														},
													],
												},
												{
													lazy: fromChunk(
														settings,
														"RequireDeviceSettings",
													),
													children: [
														{
															path: Paths.SettingsDevices,
															lazy: fromChunk(
																settings,
																"SettingsGutterLayout",
															),
															children: [
																// Both reachable without a subscription: newAccount is where
																// RequirePaidDevices sends non-paid users, and the activation
																// flow is what a user with no devices needs.
																{
																	path: Paths.SettingsDevicesNewAccount,
																	lazy: fromChunk(
																		settings,
																		"SettingsActivateView",
																	),
																},
																{
																	path: Paths.SettingsDevicesActivateSingle,
																	lazy: fromChunk(
																		settings,
																		"ActivateDeviceRoute",
																	),
																},
																{
																	path: Paths.SettingsDevicesActivate,
																	lazy: fromChunk(
																		settings,
																		"SetupSelectDevicesRoute",
																	),
																},
																{
																	path: `${Paths.SettingsDevicesSubscriptions}/:subscriptionCode/edit?`,
																	lazy: fromChunk(
																		settings,
																		"SubscriptionDetailRoute",
																	),
																},
																{
																	// The device list itself: the sidebar only offers this to paid
																	// users and sends everyone else to newAccount.
																	lazy: fromChunk(
																		settings,
																		"RequirePaidDevices",
																	),
																	children: [
																		{
																			index: true,
																			lazy: fromChunk(
																				settings,
																				"DeviceManagementRoute",
																			),
																		},
																		{
																			path: `${Paths.SettingsDevices}/:success`,
																			lazy: fromChunk(
																				settings,
																				"DeviceManagementRoute",
																			),
																		},
																	],
																},
															],
														},
													],
												},
												{
													path: "*",
													lazy: fromChunk(
														settings,
														"SettingsIndexRedirect",
													),
												},
											],
										},
									],
								},
								{ path: "*", element: <DefaultAppRedirect /> },
							],
						},
						{ path: "*", element: <DefaultAppRedirect /> },
					],
				},
			],
		},
	],
	{
		// See https://reactrouter.com/6.30.4/upgrading/future#future-flags
		future: {
			v7_relativeSplatPath: true,
			v7_fetcherPersist: true,
			v7_normalizeFormMethod: true,
			// v7_partialHydration is intentionally NOT enabled: with it, a lazy
			// index route matched on the initial page load resolves but never
			// renders (its <Navigate> redirect never fires). Revisit on v7.
			v7_skipActionErrorRevalidation: true,
		},
	},
);

registerRouter(router);
